import React, { useCallback, useMemo } from 'react';
import { compose } from 'redux';
import FiltersContentLoader from '../../components/FiltersContentLoader';
import GridHistory from '../../components/GridHistory';
import HorizontalLoader from '../../components/HorizontalLoader';
import ListSearch from '../../components/ListSearch';
import ListSearchContentLoader from '../../components/ListSearchContentLoader';
import { ENDPOINT } from '../../constants/endpoints';
import { UPGRADE_ACCOUNT_TYPE } from '../../constants/upgrade-account-types';
import { updateHistory } from '../../helpers/grid-history';
import { Permission, Role } from '../../helpers/user';
import withProcessApplyAction from '../../hocs/withProcessApplyAction';
import withViewStatisticsAction from '../../hocs/withViewStatisticsAction';
import { useGrid } from '../../hooks/useGrid';
import PageLayout from '../../layouts/PageLayout';
import ApplyJobConfirmDialog from '../../scenes/Dialog/components/ApplyJobConfirmDialog';
import BasicDialog from '../../scenes/Dialog/components/BasicDialog';
import UpgradeCandidateAccount from '../../scenes/Dialog/components/UpgradeCandidateAccount';
import UpgradeRecruiterAccount from '../../scenes/Dialog/components/UpgradeRecruiterAccount';
import List from '../../scenes/List';
import JobsItem from '../../scenes/List/components/JobsItem';
import Sidebar from '../../scenes/Sidebar';
import ActionButtonsWidget from '../../scenes/Sidebar/components/ActionButtonsWidget';
import InBounds from '../../scenes/Sidebar/components/InBounds';
import './styles.css';

const Jobs = props => {
  const {
    user,
    history,
    globalActions: { updateUserInboundsCount },
    applyActions: { check, apply },
    viewStatisticsJob,
  } = props;

  const [isRecruiter, isAllowedUseProcessAllSearchesSort] = useMemo(
    () => [
      Role.isRecruiter(user),
      Permission.isAllowedUseProcessAllSearchesSort(user.permissions),
    ],
    [user]
  );

  const grid = useGrid(ENDPOINT.COLLECTION.JOBS, params => {
    updateHistory(history, params);
  });

  const handleSort = useCallback(
    sortBy => {
      grid.actions.sortBy(sortBy);
    },
    [grid]
  );

  const handleFilter = useCallback(
    filter => {
      if (!isAllowedUseProcessAllSearchesSort && filter === 'all_searches') {
        if (UPGRADE_ACCOUNT_TYPE.RECRUITER === user.accountUpgradeType) {
          UpgradeRecruiterAccount();
        } else {
          UpgradeCandidateAccount({ history });
        }
      } else {
        grid.actions.filterBy(filter);
      }
    },
    [isAllowedUseProcessAllSearchesSort, user.accountUpgradeType, grid]
  );

  const onViewStatistics = job => {
    viewStatisticsJob(job.ProcessID);
  };

  const onApply = process => {
    const onSuccessApply = () => {
      grid.actions.setDataRows(
        grid.data.rows.map(row =>
          row.ProcessID === process.ProcessID
            ? {
                ...row,
                ApplicationProcessID: true,
              }
            : row
        )
      );
    };

    ApplyJobConfirmDialog({ id: process.Id })
      .then(() => {
        apply(process).then(success => {
          if (success) {
            onSuccessApply();
            BasicDialog({
              bodyRender: `Thank you! A recruiter will be notified that you are interested in ${process.PositionTitle} Role and get in touch with you soon.`,
            });
          }
        });
      })
      .catch(() => {
        apply(process, false).then(success => {
          if (success) {
            onSuccessApply();
            BasicDialog({
              bodyRender: `A recruiter will be notified that you are not interested in ${process.PositionTitle} Role.`,
            });
          }
        });
      });
  };

  const filterControls = [
    {
      defaultCaption: 'Eligible',
      toggledCaption: 'Eligible',
      value: 'eligible',
    },
    {
      defaultCaption: 'Applied',
      toggledCaption: 'Applied',
      value: 'applied',
    },
  ];

  if (!isRecruiter) {
    filterControls.push({
      defaultCaption: 'All searches',
      toggledCaption: 'All searches',
      value: 'all_searches',
    });
  }

  const sortControls = [
    {
      defaultCaption: 'Most Recent',
      toggledCaption: 'Most Recent',
      value: 'recent',
    },
    {
      defaultCaption: 'Start date',
      toggledCaption: 'Start date',
      value: 'start date',
    },
  ];

  return (
    <GridHistory
      history={history}
      grid={grid}
      initialParams={{
        sortBy: 'recent',
      }}
    >
      <PageLayout
        pageClass="jobs-page"
        pageTitle="Jobs"
        mainContent={
          <>
            {grid.data.init ? (
              <>
                <ListSearch
                  sortBy={grid.data.params.sortBy || sortControls[0].value}
                  sortControls={sortControls}
                  filter={grid.data.params.filter || filterControls[0].value}
                  filterControls={filterControls}
                  recordsCount={grid.data.rowsCount}
                  recordsName={grid.data.rowsCount === 1 ? 'job' : 'jobs'}
                  search={grid.data.params.search || false}
                  searchPlaceholder="Search jobs"
                  onChangeSortByHandler={handleSort}
                  onChangeFilterHandler={handleFilter}
                  onSendSearchQueryHandler={grid.actions.search}
                  isFetching={grid.data.isFetching}
                />
                <p className="jobs-page__disclaimer">
                  {`There are ${grid.data.jobsCount} live searches on GoBuyside. You are eligible to
              apply to the below positions`}
                </p>
              </>
            ) : (
              <ListSearchContentLoader />
            )}

            <div className="jobs-page__jobs-list">
              <div className="horizontal-loader__container">
                {grid.data.isFetching && <HorizontalLoader />}
              </div>
              <List
                items={grid.data.rows}
                itemRender={(item, actions) => (
                  <JobsItem
                    key={item.ProcessID}
                    item={item}
                    actions={actions}
                  />
                )}
                actions={{
                  onApply,
                  onApplyCheck: check,
                  onViewStatistics,
                  onChangePage: grid.actions.readPage,
                }}
                pagination={{
                  page: grid.data.page,
                  count: grid.data.rowsCount,
                  perPage: grid.data.perPage,
                }}
              />
            </div>
          </>
        }
        sidebarContent={
          grid.data.init ? (
            <Sidebar>
              <ActionButtonsWidget user={user} history={history} />
              <InBounds
                inboundsCount={user.inbounds}
                onInboundsUpdate={updateUserInboundsCount}
              />
            </Sidebar>
          ) : (
            <FiltersContentLoader />
          )
        }
      />
    </GridHistory>
  );
};

export default compose(withViewStatisticsAction, withProcessApplyAction)(Jobs);
